import { useQuery } from '@tanstack/react-query';
import { Suspense, lazy, useState } from 'react';
import { Link } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input } from '../../components/ui/FormField.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { api } from '../../services/api.js';
import { KIND_COLOUR, KIND_LABEL } from './PromoChart.js';
import {
  type Preset,
  type PromoDashboard,
  changePercent,
  chartBars,
  presetRange,
  promoCsv,
  rangeLabel,
  rm,
  sharePercent,
} from './promoDashboard.js';

const PromoChart = lazy(() => import('./PromoChart.js'));

/**
 * 375 (#1239, design `PromoDashboard.html`): what CARii gave away — credit from gift certificates, voucher codes and
 * personal discounts — for a period of Malaysia days, against the period before. Every figure is the server's; the
 * page only picks the period, rolls days into weeks for the chart, and writes the CSV.
 */
const PRESETS: Array<[Exclude<Preset, 'custom'>, string]> = [
  ['week', 'This week'],
  ['month', 'This month'],
  ['last-month', 'Last month'],
];
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
const TH = 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-steel';
const THN = `${TH} text-right`;
const TD = 'px-3 py-2 text-sm text-ink align-top';
const TDN = `${TD} whitespace-nowrap text-right tabular-nums`;

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function Kpi({
  testId,
  label,
  value,
  sub,
  colour,
}: {
  testId: string;
  label: string;
  value: string;
  sub: React.ReactNode;
  colour?: string;
}) {
  return (
    <Card padding="sm" data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wide text-steel">
        {colour && (
          <span
            className="mr-1.5 inline-block h-2 w-2 rounded-full"
            style={{ background: colour }}
          />
        )}
        {label}
      </p>
      <p data-testid={`${testId}-value`} className="mt-1 text-2xl font-bold text-ink">
        {value}
      </p>
      <p data-testid={`${testId}-sub`} className="text-xs text-steel">
        {sub}
      </p>
    </Card>
  );
}

function campaignTerms(c: PromoDashboard['campaigns'][number]): string {
  const value =
    c.discount_type === 'percent'
      ? `${c.discount_value}%`
      : c.discount_value !== null
        ? `${rm(c.discount_value)} off`
        : '';
  const until = c.valid_until
    ? `until ${new Date(c.valid_until).toLocaleDateString('en-GB', { timeZone: 'Asia/Kuala_Lumpur', day: 'numeric', month: 'short' })}`
    : '';
  return [value, until, c.deleted ? 'deleted' : ''].filter(Boolean).join(' · ');
}

export function PromoDashboardPage() {
  const [preset, setPreset] = useState<Preset>('month');
  const [custom, setCustom] = useState(() => presetRange('month'));
  const [by, setBy] = useState<'day' | 'week'>('day');
  const range = preset === 'custom' ? custom : presetRange(preset);
  const url = `/promotions/dashboard?${new URLSearchParams(range).toString()}`;
  const q = useQuery({
    queryKey: ['promotions-dashboard', url],
    queryFn: () => api.get(url) as Promise<PromoDashboard>,
    enabled: Boolean(range.from && range.to),
  });
  const data = q.data;
  const g = data?.given;
  const change = g ? changePercent(g.total_minor, g.previous_total_minor) : null;
  const share = g ? sharePercent(g.total_minor, g.revenue_minor) : null;
  const waiting = data ? data.pending.certificates + data.pending.discounts : 0;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <PageHeader
        title="Promo dashboard"
        description="What CARii gave away: credit from gift certificates, voucher codes and personal discounts."
      />

      <div className="flex flex-wrap items-end gap-2">
        <fieldset className="inline-flex max-w-full flex-wrap overflow-hidden rounded-md border border-hairline bg-canvas text-sm font-semibold">
          <legend className="sr-only">Period</legend>
          {[...PRESETS, ['custom', 'Custom'] as const].map(([p, label]) => (
            <button
              key={p}
              type="button"
              aria-pressed={preset === p}
              onClick={() => setPreset(p)}
              className={`whitespace-nowrap px-3 py-1.5 ${preset === p ? 'bg-brand-blue-soft text-primary-deep' : 'text-steel'}`}
            >
              {label}
            </button>
          ))}
        </fieldset>
        {preset === 'custom' && (
          <>
            <FormField label="From" name="promo-from">
              <Input
                id="promo-from"
                type="date"
                value={custom.from}
                onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
              />
            </FormField>
            <FormField label="To" name="promo-to">
              <Input
                id="promo-to"
                type="date"
                value={custom.to}
                onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
              />
            </FormField>
          </>
        )}
        <p data-testid="promo-range" className="text-sm text-steel">
          {rangeLabel(range.from, range.to)}
          {data &&
            ` · Malaysia days · compared with ${rangeLabel(data.previous.from, data.previous.to)}`}
        </p>
        <Button
          variant="secondary"
          className="ml-auto"
          disabled={!data}
          onClick={() =>
            data &&
            download(`promo-given-${data.period.from}-to-${data.period.to}.csv`, promoCsv(data))
          }
        >
          Export CSV
        </Button>
      </div>

      {q.isLoading && <Spinner />}
      {q.isError && (
        <p role="alert" data-testid="promo-error" className="text-sm text-semantic-danger-text">
          {(q.error as Error).message || 'The promo dashboard could not be loaded.'}
        </p>
      )}

      {data && g && (
        <>
          {waiting > 0 && (
            <p
              data-testid="promo-pending"
              className="flex flex-wrap items-center gap-2 rounded-md bg-semantic-warning-bg px-3 py-2 text-sm text-semantic-warning-text"
            >
              <b>{waiting} waiting for approval</b>
              <span>
                —{' '}
                {[
                  data.pending.certificates > 0
                    ? `${plural(data.pending.certificates, 'gift certificate')} (${rm(data.pending.certificates_value_minor)})`
                    : null,
                  data.pending.discounts > 0 ? plural(data.pending.discounts, 'discount') : null,
                ]
                  .filter(Boolean)
                  .join(' and ')}
                . They are not counted as given until approved.
              </span>
              {data.pending.certificates > 0 && (
                <Link
                  to="/gift-certificates?approval=pending"
                  className="font-semibold text-primary"
                >
                  Review certificates →
                </Link>
              )}
              {data.pending.discounts > 0 && (
                <Link to="/discounts?approval=pending" className="font-semibold text-primary">
                  Review discounts →
                </Link>
              )}
            </p>
          )}

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Kpi
              testId="kpi-total"
              label="Total given"
              colour="var(--color-accent-purple)"
              value={rm(g.total_minor)}
              sub={[
                change === null
                  ? 'nothing to compare with'
                  : `${change > 0 ? '▲' : change < 0 ? '▼' : '='} ${Math.abs(change)}% vs the previous period`,
                share === null ? null : `${share}% of revenue`,
              ]
                .filter(Boolean)
                .join(' · ')}
            />
            <Kpi
              testId="kpi-credit"
              label={KIND_LABEL.credit}
              colour={KIND_COLOUR.credit}
              value={rm(g.credit_minor)}
              sub={`on ${plural(g.credit_bookings, 'booking')}`}
            />
            <Kpi
              testId="kpi-voucher"
              label={KIND_LABEL.voucher}
              colour={KIND_COLOUR.voucher}
              value={rm(g.voucher_minor)}
              sub={`${plural(g.voucher_bookings, 'code')} used · ${plural(g.voucher_campaigns, 'campaign')}`}
            />
            <Kpi
              testId="kpi-personal"
              label={KIND_LABEL.personal}
              colour={KIND_COLOUR.personal}
              value={rm(g.personal_minor)}
              sub={`${plural(g.personal_bookings, 'booking')} · ${plural(g.personal_customers, 'customer')}`}
            />
            <Kpi
              testId="kpi-issued"
              label="Credit issued"
              value={rm(data.certificates.issued.value_minor)}
              sub={`${plural(data.certificates.issued.count, 'certificate')} approved in the period`}
            />
            <Kpi
              testId="kpi-outstanding"
              label="Credit outstanding"
              value={rm(data.certificates.outstanding.value_minor)}
              sub={`today · ${rm(data.certificates.expiring_within_30_days.value_minor)} expires within 30 days`}
            />
          </div>

          <Card>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-steel">
                Given per {by}
              </h2>
              <fieldset className="ml-auto inline-flex overflow-hidden rounded-md border border-hairline text-xs font-semibold">
                <legend className="sr-only">Chart by</legend>
                {(['day', 'week'] as const).map((b) => (
                  <button
                    key={b}
                    type="button"
                    aria-pressed={by === b}
                    onClick={() => setBy(b)}
                    className={`px-3 py-1 ${by === b ? 'bg-brand-blue-soft text-primary-deep' : 'text-steel'}`}
                  >
                    {b === 'day' ? 'Day' : 'Week'}
                  </button>
                ))}
              </fieldset>
            </div>
            <Suspense fallback={<div className="h-[200px]" />}>
              <PromoChart bars={chartBars(data.days, by)} />
            </Suspense>
            <div className="mt-2 flex flex-wrap gap-4 text-xs text-steel">
              {(['credit', 'voucher', 'personal'] as const).map((k) => (
                <span key={k}>
                  <span
                    className="mr-1.5 inline-block h-2 w-2 rounded-full"
                    style={{ background: KIND_COLOUR[k] }}
                  />
                  {KIND_LABEL[k]}
                </span>
              ))}
              <span>Hover a bar for its figures</span>
            </div>
          </Card>

          <div className="grid min-w-0 gap-4 lg:grid-cols-2 [&>*]:min-w-0">
            <Card>
              <div className="mb-2 flex items-center gap-2">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-steel">
                  Voucher campaigns
                </h2>
                <span className="ml-auto text-xs text-steel">in the period</span>
              </div>
              {data.campaigns.length === 0 ? (
                <p data-testid="promo-no-campaigns" className="text-sm text-muted">
                  No voucher code was used in this period.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table data-testid="promo-campaigns" className="w-full">
                    <thead>
                      <tr>
                        <th className={TH}>Campaign</th>
                        <th className={THN}>Codes used</th>
                        <th className={THN}>Given</th>
                        <th className={TH}>Limit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.campaigns.map((c) => (
                        <tr key={c.id} className="border-t border-hairline">
                          <td className={TD}>
                            <Link
                              to={`/discounts/${c.id}`}
                              className="text-primary hover:underline"
                            >
                              {c.name ?? 'Unnamed campaign'}
                            </Link>
                            <div className="text-xs text-steel">{campaignTerms(c)}</div>
                          </td>
                          <td className={TDN}>{c.codes_used}</td>
                          <td className={TDN}>{rm(c.given_minor)}</td>
                          <td className={TD}>
                            {c.limit === null ? (
                              'no limit'
                            ) : (
                              <span className="flex flex-wrap items-center gap-1.5">
                                {c.at_limit && (
                                  <span className="rounded-full bg-semantic-danger-bg px-2 py-0.5 text-xs font-bold text-semantic-danger-text">
                                    at limit
                                  </span>
                                )}
                                {c.redemptions_count} / {c.limit}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <Link
                to="/discounts"
                className="mt-2 inline-block text-sm font-semibold text-primary"
              >
                All campaigns →
              </Link>
            </Card>

            <Card>
              <div className="mb-2 flex items-center gap-2">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-steel">
                  Gift certificates
                </h2>
                <span className="ml-auto text-xs text-steel">approved certificates</span>
              </div>
              <div className="overflow-x-auto">
                <table data-testid="promo-certificates" className="w-full">
                  <thead>
                    <tr>
                      <th className={TH} />
                      <th className={THN}>Certificates</th>
                      <th className={THN}>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(
                      [
                        ['Issued in the period', data.certificates.issued, ''],
                        ['Used in the period', data.certificates.used, ''],
                        ['Given back (cancelled before pickup)', data.certificates.returned, '− '],
                        ['Outstanding today', data.certificates.outstanding, ''],
                        ['Expires within 30 days', data.certificates.expiring_within_30_days, ''],
                        ['Expired unused, in the period', data.certificates.expired_unused, ''],
                      ] as const
                    ).map(([label, a, sign]) => (
                      <tr key={label} className="border-t border-hairline">
                        <td className={TD}>
                          {label === 'Outstanding today' ? <b>{label}</b> : label}
                        </td>
                        <td className={TDN}>{a.count}</td>
                        <td className={TDN}>
                          {sign}
                          {rm(a.value_minor)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Link
                to="/gift-certificates"
                className="mt-2 inline-block text-sm font-semibold text-primary"
              >
                All gift certificates →
              </Link>
            </Card>
          </div>

          <Card>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-steel">
                Biggest receivers
              </h2>
              <span className="text-xs text-steel">
                customers given the most in the period, all three kinds
              </span>
              <span className="ml-auto text-xs text-steel">top 10</span>
            </div>
            {data.receivers.length === 0 ? (
              <p data-testid="promo-no-receivers" className="text-sm text-muted">
                Nothing was given to any customer in this period.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table data-testid="promo-receivers" className="w-full min-w-[560px]">
                  <thead>
                    <tr>
                      <th className={TH}>Customer</th>
                      <th className={THN}>Bookings</th>
                      <th className={THN}>Credit</th>
                      <th className={THN}>Vouchers</th>
                      <th className={THN}>Personal</th>
                      <th className={THN}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.receivers.map((r) => (
                      <tr key={r.user_id} className="border-t border-hairline">
                        <td className={TD}>
                          <Link to={`/users/${r.user_id}`} className="text-primary hover:underline">
                            {r.name ?? 'Account removed'}
                          </Link>
                        </td>
                        <td className={TDN}>{r.bookings}</td>
                        <td className={TDN}>{r.credit_minor ? rm(r.credit_minor) : '—'}</td>
                        <td className={TDN}>{r.voucher_minor ? rm(r.voucher_minor) : '—'}</td>
                        <td className={TDN}>{r.personal_minor ? rm(r.personal_minor) : '—'}</td>
                        <td className={TDN}>
                          <b>{rm(r.total_minor)}</b>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <p className="text-xs text-steel">
            How it counts: discounts and credit are counted on <b>paid bookings by pickup day</b>{' '}
            (the Sales Statement's rule), so a booking cancelled before pickup never counts.
            Certificates count on the day they were approved, used, given back or expired.
            Waiting-for-approval and rejected ones are never counted. Platform-wide view for
            administrators.
          </p>
        </>
      )}
    </div>
  );
}
